import React, { useState, useEffect } from "react";
import { X, UserPlus, Sparkles, User, School, GraduationCap, Code2, Edit3, Calendar } from "lucide-react";
import { collection, doc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../../../config/firebaseConfig";

const GRADE_OPTIONS = [
  { value: "Class 1", label: "Class 1" },
  { value: "Class 2", label: "Class 2" },
  { value: "Class 3", label: "Class 3" },
  { value: "Class 4", label: "Class 4" },
  { value: "Class 5", label: "Class 5" },
  { value: "Class 6", label: "Class 6" },
  { value: "Class 7", label: "Class 7" },
  { value: "Class 8", label: "Class 8" },
  { value: "Class 9", label: "Class 9" },
  { value: "Class 10", label: "Class 10" },
  { value: "Class 11", label: "Class 11" },
  { value: "Class 12", label: "Class 12" },
];

export const WORKSPACE_OPTIONS = [
  {
    value: "scratch_jr",
    label: "Practido Junior Blocks",
    desc: "Visual blocks for young coders (Classes 1-3)",
    icon: "🧩",
    disabled: true,
    badge: "Coming Soon",
  },
  {
    value: "scratch",
    label: "Practido Blocks",
    desc: "Interactive block programming (Classes 4-8)",
    icon: "🐱",
    disabled: false,
  },
  {
    value: "python",
    label: "Practido Code",
    desc: "Text-based coding & multi-language studio (Classes 9+)",
    icon: "💻",
    disabled: false,
  },
];

const AddChildModal = ({ isOpen, onClose, onChildAdded, onChildUpdated, parentsDetails, childToEdit = null }) => {
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [grade, setGrade] = useState("Class 1");
  const [workspace, setWorkspace] = useState("scratch");
  const [school, setSchool] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isEditMode = Boolean(childToEdit);

  useEffect(() => {
    if (childToEdit) {
      setName(childToEdit.name || "");
      setDob(childToEdit.dob || "");
      setGrade(childToEdit.grade || childToEdit.studentClass || childToEdit.class || "Class 1");
      setSchool(childToEdit.school || "");
      setWorkspace(childToEdit.workspace || "scratch");
    } else {
      setName("");
      setDob("");
      setGrade("Class 1");
      setSchool("");
      setWorkspace("scratch");
    }
  }, [childToEdit, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Please enter the child's full name.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const parentId =
        auth?.currentUser?.uid || parentsDetails?.id || parentsDetails?.uid;

      if (!parentId) {
        throw new Error("Parent account session not found. Please log in again.");
      }

      if (isEditMode) {
        const targetId = childToEdit.id || childToEdit.uid;
        const studentRef = doc(db, "students", targetId);
        const updateData = {
          name: name.trim(),
          dob: dob || "",
          grade: grade,
          class: grade,
          studentClass: grade,
          school: school.trim(),
          workspace: workspace,
          updatedAt: serverTimestamp(),
        };

        await updateDoc(studentRef, updateData);

        const updatedStudent = { ...childToEdit, ...updateData };
        if (onChildUpdated) {
          onChildUpdated(updatedStudent);
        }
        onClose();
      } else {
        const newStudentRef = doc(collection(db, "students"));
        const newStudentData = {
          id: newStudentRef.id,
          name: name.trim(),
          dob: dob || "",
          grade: grade,
          class: grade,
          studentClass: grade,
          school: school.trim(),
          workspace: workspace,
          parentUid: parentId,
          createdAt: serverTimestamp(),
        };

        await setDoc(newStudentRef, newStudentData);

        setName("");
        setDob("");
        setSchool("");
        setGrade("Class 1");
        setWorkspace("scratch");

        if (onChildAdded) {
          onChildAdded(newStudentData);
        }
        onClose();
      }
    } catch (err) {
      console.error("Error saving student details:", err);
      setError(err.message || "Failed to save student details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl space-y-5 animate-in zoom-in-95 duration-200 border border-slate-100 font-sans text-slate-800 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
              {isEditMode ? <Edit3 className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900">
                {isEditMode ? "Edit Child Details" : "Add New Child"}
              </h3>
              <p className="text-xs text-slate-500 font-medium">
                {isEditMode ? "Update details & workspace for this student" : "Enter details & choose their primary coding workspace"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-2xl text-xs font-bold">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div className="space-y-1.5 text-left">
            <label className="text-xs font-extrabold text-slate-700 flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-purple-600" />
              <span>Child Full Name *</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Aarav Sharma"
              className="w-full px-4 py-2.5 rounded-2xl border border-slate-200 text-sm font-semibold text-slate-900 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
            />
          </div>

          {/* Class / Grade Field */}
          <div className="space-y-1.5 text-left">
            <label className="text-xs font-extrabold text-slate-700 flex items-center gap-1.5">
              <GraduationCap className="w-3.5 h-3.5 text-purple-600" />
              <span>Student Class / Grade *</span>
            </label>
            <select
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className="w-full px-4 py-2.5 rounded-2xl border border-slate-200 text-sm font-semibold text-slate-900 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition cursor-pointer"
            >
              {GRADE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Primary Workspace Selection Field */}
          <div className="space-y-1.5 text-left">
            <label className="text-xs font-extrabold text-slate-700 flex items-center gap-1.5">
              <Code2 className="w-3.5 h-3.5 text-purple-600" />
              <span>Choose your Workspace *</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
              {WORKSPACE_OPTIONS.map((opt) => {
                const isSelected = workspace === opt.value;
                const isDisabled = opt.disabled;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      if (!isDisabled) setWorkspace(opt.value);
                    }}
                    className={`p-3 rounded-2xl border text-left flex flex-col justify-between gap-2 transition-all ${
                      isDisabled
                        ? "opacity-60 bg-slate-50 border-slate-200 cursor-not-allowed"
                        : isSelected
                        ? "border-purple-600 bg-purple-50/80 ring-2 ring-purple-200 shadow-xs cursor-pointer"
                        : "border-slate-200 hover:border-purple-300 hover:bg-slate-50 cursor-pointer"
                    }`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-xl leading-none">{opt.icon}</span>
                      {opt.badge && (
                        <span className="bg-amber-100 text-amber-800 text-[9px] font-black px-2 py-0.5 rounded-full">
                          {opt.badge}
                        </span>
                      )}
                    </div>
                    <div className="space-y-0.5 min-w-0 flex-1">
                      <h4 className={`text-xs font-black leading-tight ${isSelected ? "text-purple-900" : "text-slate-800"}`}>
                        {opt.label}
                      </h4>
                      <p className="text-[10px] text-slate-500 line-clamp-2 leading-tight font-medium">
                        {opt.desc}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date of Birth Field */}
          <div className="space-y-1.5 text-left">
            <label className="text-xs font-extrabold text-slate-700 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-purple-600" />
              <span>Date of Birth (Optional)</span>
            </label>
            <input
              type="date"
              value={dob}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setDob(e.target.value)}
              className="w-full px-4 py-2.5 rounded-2xl border border-slate-200 text-sm font-semibold text-slate-900 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition bg-white"
            />
          </div>

          {/* School Name Field */}
          <div className="space-y-1.5 text-left">
            <label className="text-xs font-extrabold text-slate-700 flex items-center gap-1.5">
              <School className="w-3.5 h-3.5 text-purple-600" />
              <span>School Name (Optional)</span>
            </label>
            <input
              type="text"
              value={school}
              onChange={(e) => setSchool(e.target.value)}
              placeholder="e.g. Delhi Public School"
              className="w-full px-4 py-2.5 rounded-2xl border border-slate-200 text-sm font-semibold text-slate-900 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
            />
          </div>

          {/* Action Buttons */}
          <div className="pt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-1/3 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-xs rounded-2xl transition cursor-pointer"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="w-2/3 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-extrabold text-xs rounded-2xl shadow-lg shadow-purple-500/20 flex items-center justify-center gap-2 transition cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <span>Saving Details...</span>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>{isEditMode ? "Save Changes" : "Save & Open Studio 🚀"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddChildModal;

